/**
 * Complete the Set helpers, shared by the cart store, the add-to-cart actions and the product page rows.
 *
 * Items added through Complete the Set carry the line item property `_complete_the_set_anchor`, set to the
 * ID of the product they complete. The "Complete the set" discount function (app repository
 * mindarc/razorback-complete-the-set-discount) takes the shop's percentage off those lines while that
 * product is in the cart at full price. The percentage is the shop metafield
 * `custom.complete_the_set_discount_percentage`, output to `window.themeSettings` by layout/theme.liquid.
 */

export const ANCHOR_PROPERTY = '_complete_the_set_anchor';
export const ANCHOR_PARAMETER = 'complete_the_set_anchor';

// The discount function ignores anything outside 1–50, so the theme treats it as no discount too.
const MAX_PERCENTAGE = 50;

/**
 * The Complete the set percentage, or 0 when it is missing or outside what the discount accepts.
 * @param {unknown} value
 * @returns {number}
 */
export function completeTheSetPercentage(value = globalThis.themeSettings?.completeTheSetPercentage) {
  const percentage = Number(value);
  return percentage > 0 && percentage <= MAX_PERCENTAGE ? percentage : 0;
}

/**
 * A price in cents after the Complete the set percentage, rounded to the cent.
 * @param {number} cents
 * @param {number} [percentage]
 * @returns {number}
 */
export function discountedPrice(cents, percentage = completeTheSetPercentage()) {
  return cents - Math.round((cents * percentage) / 100);
}

/**
 * The anchor product ID a page was opened with (`?complete_the_set_anchor=`), or '' when there is none,
 * it isn't a numeric ID, or it names the product being viewed.
 * @param {string|number} productId
 * @param {string} [search]
 * @returns {string}
 */
export function anchorFromUrl(productId, search = globalThis.location?.search ?? '') {
  const anchor = new URLSearchParams(search).get(ANCHOR_PARAMETER) ?? '';
  return /^\d+$/.test(anchor) && anchor !== String(productId) ? anchor : '';
}

// Rows in blocks set to ride along with the product form's Add to cart (not "On its own button")
export const RIDE_ALONG_ROWS_SELECTOR = '[data-complete-the-set-rows][data-add-mode="with_main"]';

/**
 * The ticked Complete the Set rows for a product, as cart items that carry the anchor property.
 * @param {string|number} productId
 * @param {string|number} anchorProductId
 * @param {{root?: ParentNode, containerSelector?: string}} [options] containerSelector limits the rows to
 *   those inside matching blocks, e.g. RIDE_ALONG_ROWS_SELECTOR
 * @returns {{id: string, quantity: number, properties: Record<string, string>}[]}
 */
export function tickedCompleteTheSetItems(productId, anchorProductId, { root = document, containerSelector = '' } = {}) {
  const rowSelector = `[data-complete-the-set-item][data-complete-the-set-for="${productId}"]:checked:not(:disabled)`;
  const selector = containerSelector ? `${containerSelector} ${rowSelector}` : rowSelector;
  return Array.from(root.querySelectorAll(selector), (input) => ({
    id: input.value,
    quantity: 1,
    properties: { [ANCHOR_PROPERTY]: String(anchorProductId) },
  }));
}
